import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { getLoginRole } from "utils/login";
import { ID_CARD_ALLOWED_ROLES } from "utils/constants";
import EventService from "services/event/event.service";
import HFNLoader from "sharedComponents/lazyLoading/Loading";
import { HFNButton } from "@hfn-uicore/core";
import "./PrintIdCard.scss";

function PrintIDCard() {
  const { event } = useParams();
  const { cardNumber } = useParams();
  const loginRole = useMemo(() => getLoginRole() || [], []);
  const [userAsPrefect, setUserAsPrefect] = useState(false);
  const [idCard, setIdCard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const service = useMemo(() => new EventService(), []);
  const pollTimer = useRef(null);

  useEffect(() => {
    const isPrefect = ID_CARD_ALLOWED_ROLES.some((role) =>
      loginRole.includes(role)
    );
    if (isPrefect) setUserAsPrefect(true);

    if (cardNumber && event && isPrefect) {
      setLoading(true);

      const fetchCard = () =>
        service.getIdCardDetails(event, cardNumber).then((res) => res.data);

      fetchCard()
        .then((data) => {
          setIdCard(data);
          if (!data?.card_front_image_url && !data?.card_back_image_url) {
            setImagesLoading(true);
            const startTime = Date.now();

            const poll = () => {
              if (Date.now() - startTime >= 2 * 60 * 1000) {
                setImagesLoading(false);
                return;
              }
              fetchCard()
                .then((d) => {
                  if (d?.card_front_image_url || d?.card_back_image_url) {
                    setIdCard(d);
                    setImagesLoading(false);
                  } else {
                    pollTimer.current = setTimeout(poll, 3000);
                  }
                })
                .catch(() => setImagesLoading(false));
            };

            pollTimer.current = setTimeout(poll, 3000);
          }
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }

    return () => clearTimeout(pollTimer.current);
  }, [loginRole, cardNumber, event]);

  const handleDownload = () => {
    if (downloading) return;
    setDownloading(true);
    const items = [
      { url: idCard?.card_front_image_url, filename: "id-card-front.png" },
      { url: idCard?.card_back_image_url, filename: "id-card-back.png" },
    ].filter((item) => item.url);

    Promise.all(
      items.map(({ url, filename }) =>
        fetch(url)
          .then((res) => res.blob())
          .then((blob) => {
            const objectUrl = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = objectUrl;
            a.download = filename;
            a.click();
            setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
          })
      )
    ).finally(() => setDownloading(false));
  };

  if (loading || imagesLoading) {
    return <HFNLoader />;
  }

  return (
    <div>
      {userAsPrefect && idCard && (
        <div>
          <div className="print-id-card__images">
            {idCard.card_front_image_url && (
              <div className="print-id-card__card">
                <img
                  src={idCard.card_front_image_url}
                  alt="ID Card Front"
                  className="print-id-card__card-image"
                />
              </div>
            )}
            {idCard.card_back_image_url && (
              <div className="print-id-card__card">
                <img
                  src={idCard.card_back_image_url}
                  alt="ID Card Back"
                  className="print-id-card__card-image"
                />
              </div>
            )}
          </div>
          <div className="print-id-card__actions">
            <HFNButton
              label={downloading ? "Downloading..." : "Download"}
              onClick={handleDownload}
              disabled={downloading}
            />
          </div>
        </div>
      )}
      {!userAsPrefect && (
        <div>You do not have permission to perform this action.</div>
      )}
    </div>
  );
}

export default PrintIDCard;
